import React, { useMemo } from 'react';
import _startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { getMachineScheduleWooRefs } from 'lib/selectors/getMachineSchedule';
import { getMachine } from 'lib/selectors/getMachine';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getMachines } from 'lib/selectors/getMachines';

/**
 * WorkOrderOpMachineName displays the name of the machine assigned to a work order operation.
 * If the operation is scheduled on the current machine, it shows that machine's name.
 * Otherwise, it shows the resourceId from the work order operation, or "None" if no resource is assigned.
 *
 * @param {Object} props
 * @param {Object} props.workOrderOp - The work order operation object
 * @param {string} props.workOrderOp.workOrderOperationRef - Reference ID for the work order operation
 * @param {Object} props.workOrderOp.resource - Resource information for the operation
 * @param {string} props.workOrderOp.resource.resourceId - ID of the assigned resource/machine
 * @returns {React.ReactElement} The machine name display component
 */
const WorkOrderOpMachineName = ({ workOrderOp }) => {
  const { t } = useTranslation();

  // Get the current machine and list of scheduled operation refs from Redux
  const machine = useSelector(getMachine);
  const machines = useSelector(getMachines);
  const machineScheduleWooRefs = useSelector(getMachineScheduleWooRefs);

  // Determine the name to display based on scheduling and assignment
  const name = useMemo(() => {
    // If operation is scheduled on current machine, show current machine name
    const { resource, workOrderOperationRef } = workOrderOp || {};

    if (
      machine &&
      (machineScheduleWooRefs.includes(workOrderOperationRef) ||
        resource?.machineRef === machine?.machineRef)
    ) {
      return machine.name;
    }

    const selectedMachine = machines?.find((m) => {
      return (
        m?.erpMachineId === resource?.resourceId ||
        m?.machineRef === resource?.machineRef
      );
    });

    const machineName =
      selectedMachine?.name || resource?.resourceId || _startCase(t('none'));

    // Otherwise show assigned resource ID or "None"
    return machineName;
  }, [t, workOrderOp, machineScheduleWooRefs, machine, machines]);

  return <>{name}</>;
};

WorkOrderOpMachineName.propTypes = {
  workOrderOp: PropTypes.object,
};

export default WorkOrderOpMachineName;
